import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { adminApi, apiErrorDetail } from "../lib/adminApi";
import { AdminLogin } from "../components/admin/AdminLogin";
import { ContactCard } from "../components/admin/ContactCard";
import { AdminHeader, FilterBar } from "../components/admin/AdminChrome";

const matchesFilter = (c, filter) => {
    if (filter === "alle") return true;
    if (filter === "offen") return !c.erledigt;
    return c.erledigt;
};

export default function Admin() {
    const [authed, setAuthed] = useState(null);
    const [contacts, setContacts] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("offen");

    const logout = useCallback(async () => {
        try {
            await adminApi.post("/admin/logout");
        } catch {
            /* Sitzung lokal trotzdem beenden */
        }
        setAuthed(false);
        setContacts(null);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/admin/contacts");
            setContacts(data);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error(apiErrorDetail(err, "Bitte erneut anmelden."));
                setAuthed(false);
                setContacts(null);
            } else {
                toast.error("Anfragen konnten nicht geladen werden.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        adminApi.get("/admin/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
    }, []);

    useEffect(() => {
        if (authed) load();
    }, [authed, load]);

    const onDelete = async (c) => {
        if (!window.confirm(`Anfrage von ${c.vorname} ${c.name} endgültig löschen?`)) return;
        try {
            await adminApi.delete(`/admin/contacts/${c.id}`);
            setContacts((list) => list.filter((x) => x.id !== c.id));
            toast.success("Anfrage gelöscht.");
        } catch {
            toast.error("Löschen fehlgeschlagen.");
        }
    };

    const onToggle = async (c) => {
        const next = !c.erledigt;
        try {
            await adminApi.patch(`/admin/contacts/${c.id}`, { erledigt: next });
            setContacts((list) => list.map((x) => (x.id === c.id ? { ...x, erledigt: next } : x)));
        } catch {
            toast.error("Status konnte nicht gespeichert werden.");
        }
    };

    if (authed === null) {
        return (
            <div className="min-h-screen bg-[#F4F1EA] flex items-center justify-center">
                <Loader2 size={20} className="animate-spin text-[#8C8275]" />
            </div>
        );
    }
    if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

    const offen = contacts?.filter((c) => !c.erledigt).length ?? 0;
    const total = contacts?.length ?? null;
    const shown = contacts?.filter((c) => matchesFilter(c, filter)) ?? null;
    const emptyText = filter === "offen" ? "Alles beantwortet – keine offenen Anfragen." : "Keine Einträge.";

    return (
        <div className="min-h-screen bg-[#F4F1EA]" data-testid="admin-dashboard">
            <AdminHeader offen={offen} total={total} loading={loading} onRefresh={load} onLogout={logout} />
            <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10 space-y-5">
                <FilterBar filter={filter} setFilter={setFilter} offen={offen} done={(total ?? 0) - offen} />
                {contacts === null && (
                    <p className="font-sans text-sm text-[#8C8275] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Lade Anfragen …</p>
                )}
                {shown?.length === 0 && (
                    <p data-testid="admin-empty" className="font-serif text-2xl font-light text-[#8C8275] py-16 text-center">{emptyText}</p>
                )}
                {shown?.map((c) => <ContactCard key={c.id} c={c} onDelete={onDelete} onToggle={onToggle} />)}
            </main>
        </div>
    );
}
